/**
 * ERPNext Authentication Service
 */

const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';

export interface User {
  email: string;
  full_name: string;
  user_image?: string;
}

/**
 * Login user with ERPNext
 */
export async function login(email: string, password: string): Promise<User> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    const response = await fetch(`${baseUrl}/api/method/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        usr: email,
        pwd: password,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    // After successful login, get user info
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('Failed to get user info after login');
    }
    return user;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Signup new user (sends email verification link)
 */
export async function signup(email: string, fullName: string): Promise<void> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    const response = await fetch(`${baseUrl}/api/method/frappe.core.doctype.user.user.sign_up`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        full_name: fullName,
        redirect_to: '/',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Signup failed');
    }

    // User will receive email with link to set password and verify account
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    await fetch(`${baseUrl}/api/method/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
}

/**
 * Get current logged-in user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    const response = await fetch(`${baseUrl}/api/method/frappe.auth.get_logged_user`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    // Guest user
    if (data.message === 'Guest') {
      return null;
    }

    // Get full user details
    const userResponse = await fetch(
      `${baseUrl}/api/resource/User/${encodeURIComponent(data.message)}?fields=["email","full_name","user_image"]`,
      {
        credentials: 'include',
      }
    );

    if (!userResponse.ok) {
      return null;
    }

    const userData = await userResponse.json();

    return {
      email: userData.data.email,
      full_name: userData.data.full_name,
      user_image: userData.data.user_image,
    };
  } catch (error) {
    console.error('Get user error:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}

/**
 * Request password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    const response = await fetch(`${baseUrl}/api/method/frappe.core.doctype.user.user.reset_password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user: email,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password reset request failed');
    }

    // ERPNext sends password reset email
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
}

/**
 * Update password using verification key from email
 */
export async function updatePassword(key: string, newPassword: string): Promise<void> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    const response = await fetch(`${baseUrl}/api/method/frappe.core.doctype.user.user.update_password`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        key: key,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Password update failed');
    }

    // ERPNext returns user info and redirect URL on success
    // Response format: {"message": "/", "home_page": "/me", "full_name": "..."}
    const data = await response.json();

    // Success if we get a response with message (redirect URL) or full_name
    if (!data.message && !data.full_name) {
      throw new Error('Password update failed');
    }
  } catch (error) {
    console.error('Update password error:', error);
    throw error;
  }
}
